import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function LettersSection() {
    const [letters, setLetters] = useState([]);
    const [name, setName] = useState("");
    const [message, setMessage] = useState("");
    const [sending, setSending] = useState(false);
    const [expanded, setExpanded] = useState(false);

    const fetchLetters = async () => {
        try {
            const { data } = await api.get("/letters");
            setLetters(data.letters);
        } catch (e) {
            /* the wall is ornamental */
        }
    };

    useEffect(() => {
        fetchLetters();
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (!message.trim()) return;
        setSending(true);
        try {
            await api.post("/letters", { name: name.trim(), message: message.trim() });
            toast.success("Your letter has arrived. It will rest in the garden once the author has read it.");
            setName("");
            setMessage("");
            fetchLetters();
        } catch (err) {
            toast.error("The wind carried your letter away. Please try once more.");
        } finally {
            setSending(false);
        }
    };

    return (
        <section id="letters" data-testid="letters-section" className="relative mx-auto max-w-6xl px-6 py-32">
            <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 1 }}
                className="text-center"
            >
                <p className="font-cinzel text-xs tracking-[0.4em] text-[#b89860] uppercase">
                    The Wall of Remembrance
                </p>
                <h2 className="font-cinzel mt-6 text-3xl leading-snug text-[#f3e5ab] sm:text-5xl">
                    Letters to Our Mothers
                </h2>
                <p className="font-cormorant mx-auto mt-5 max-w-xl text-lg italic text-[#a3b899]">
                    Leave one sentence for the woman who first taught you love.
                    It will rest here, among the roses.
                </p>
                <p className="font-cormorant mx-auto mt-3 max-w-xl text-sm italic text-[#8a7a60]">
                    Every letter is read by the author before it appears — only kindness rests here.
                </p>
            </motion.div>

            <motion.form
                onSubmit={submit}
                data-testid="letter-form"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.9, delay: 0.15 }}
                className="mx-auto mt-14 max-w-xl space-y-4"
            >
                <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your first name (optional)"
                    maxLength={60}
                    data-testid="letter-name-input"
                    className="input-eden font-cormorant w-full rounded-sm px-5 py-4 text-lg"
                />
                <div>
                    <textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        required
                        maxLength={280}
                        rows={3}
                        placeholder="One sentence, from the heart..."
                        data-testid="letter-message-input"
                        className="input-eden font-cormorant w-full resize-none rounded-sm px-5 py-4 text-lg"
                    />
                    <p className="font-cormorant mt-1 text-right text-xs italic text-[#8a7a60]">
                        {message.length}/280
                    </p>
                </div>
                <button
                    type="submit"
                    disabled={sending}
                    data-testid="letter-submit-button"
                    className={`btn-eden w-full rounded-sm bg-gradient-to-br from-[rgba(160,120,60,0.9)] to-[rgba(120,85,40,0.9)] px-8 py-4 text-xs text-[#f5e6c8] ${sending ? "animate-pulse opacity-70" : ""}`}
                >
                    {sending ? "Placing the Rose..." : "Leave Your Letter"}
                </button>
            </motion.form>

            <div className="mt-20 columns-1 gap-6 sm:columns-2 lg:columns-3" data-testid="letters-wall">
                <AnimatePresence>
                    {(expanded ? letters : letters.slice(0, 5)).map((l, i) => (
                        <div
                            key={`${l.created_at}-${i}`}
                            className={`mb-6 break-inside-avoid ${i % 2 === 0 ? "-rotate-[0.7deg]" : "rotate-[0.7deg]"}`}
                        >
                            <motion.div
                                data-testid={`letter-card-${i}`}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.8, delay: (i % 3) * 0.12 }}
                                className="letter-note rounded-sm p-6"
                            >
                                <p className="font-cormorant text-lg italic leading-relaxed">
                                    &ldquo;{l.message}&rdquo;
                                </p>
                                <p className="font-cinzel mt-4 text-[0.65rem] tracking-[0.3em] text-[#8a6f3c] uppercase">
                                    — {l.name}
                                </p>
                            </motion.div>
                        </div>
                    ))}
                </AnimatePresence>
            </div>

            {letters.length > 5 && (
                <div className="mt-6 text-center">
                    <button
                        data-testid="letters-reveal-button"
                        onClick={() => setExpanded((v) => !v)}
                        className="btn-eden rounded-sm bg-transparent px-6 py-3 text-[0.65rem] text-[#d4af37]"
                    >
                        {expanded ? "Let Them Rest Again" : `Reveal ${letters.length - 5} More Letters`}
                    </button>
                </div>
            )}
        </section>
    );
}
